import React from "react";

import { useTranslation } from "react-i18next";

import InfoTooltip from "../../utils/InfoTooltip";

import type { ChangeEvent, Dispatch, JSX, SetStateAction } from "react";

import type {
  Project,
  SocialNetworkLink,
  TrackerConfig,
  UpdatedProject,
  UpdatedSocialNetworkLink,
} from "../../../api/ProjectAPI.types";

const TRACKER_LIST = [
  "google_analytics",
  "facebook_domain",
  "facebook_pixel",
  "tiktok_pixel",
  "google_domain",
  "google_tag_manager",
  "global_site_tag",
  "matomo",
];

const INTEGRATION_TAG_LIST = ["beamer", "tapfiliate", "wonderpush"];

interface ProjectAdminFormSocialTrackingAndSocialNetworkProps {
  project: Project | UpdatedProject;
  setProject: Dispatch<SetStateAction<UpdatedProject>>;
}

function ProjectAdminFormSocialTrackingAndSocialNetwork({
  project,
  setProject,
}: ProjectAdminFormSocialTrackingAndSocialNetworkProps): JSX.Element {
  const { t } = useTranslation("editor-project");

  const getTrackerConfig = (
    trackerType: string,
  ): TrackerConfig | Partial<TrackerConfig> =>
    project?.trackers?.find((tracker) => tracker.type === trackerType)
      ?.config ?? {
      id: "",
      siteUrl: "",
    };

  const getSocialLink = (
    socialName: string,
  ): SocialNetworkLink | UpdatedSocialNetworkLink =>
    project.social_network_links.find(
      (socialNetworkLink) => socialNetworkLink.social_network === socialName,
    ) ?? {
      social_network: socialName,
      uid: "",
    };

  const handleChangeTracker = (
    target: ChangeEvent<HTMLInputElement>["target"],
    trackerType: string,
    field: keyof TrackerConfig = "id",
  ): void => {
    const isTrackerExist = project.trackers.some(
      (tracker) => tracker.type === trackerType,
    );

    if (isTrackerExist) {
      const updatedTrackers = project.trackers.map((tracker) => {
        if (tracker.type === trackerType) {
          return {
            ...tracker,
            config: {
              ...tracker.config,
              [field]: target.value,
            },
          };
        }
        return tracker;
      });

      setProject({
        ...project,
        trackers: updatedTrackers,
      });
    } else {
      const updatedTrackers = [
        ...project.trackers,
        {
          type: trackerType,
          config: {
            [field]: target.value,
          },
        },
      ];
      setProject({
        ...project,
        trackers: updatedTrackers,
      });
    }
  };

  const handleChangeSocialLink = (
    target: ChangeEvent<HTMLInputElement>["target"],
    socialName: string,
  ): void => {
    const isSocialLinkExist = project.social_network_links.some(
      (socialNetworkLink) => socialNetworkLink.social_network === socialName,
    );

    if (isSocialLinkExist) {
      const updatedSocialNetworkLinks = project.social_network_links.map(
        (socialNetworkLink) => {
          if (socialNetworkLink.social_network === socialName) {
            return {
              ...socialNetworkLink,
              uid: target.value,
            };
          }
          return socialNetworkLink;
        },
      );

      setProject({
        ...project,
        social_network_links: updatedSocialNetworkLinks,
      });
    } else {
      const updatedSocialNetworkLinks = [
        ...project.social_network_links,
        {
          social_network: socialName,
          uid: target.value,
        },
      ];
      setProject({
        ...project,
        social_network_links: updatedSocialNetworkLinks,
      });
    }
  };

  return (
    <React.Fragment>
      <div className="editor-form-section section-separator">
        <div className="editor-form-translates">
          <div className="editor-form-section-title">{t("tracking")}</div>
          {TRACKER_LIST.map((trackerName) => (
            <div key={trackerName} className="editor-form-translate">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label>{t(trackerName)}</label>
              <input
                className="editor-form-input-title"
                type="text"
                value={getTrackerConfig(trackerName)?.id}
                onChange={(e): void => {
                  handleChangeTracker(e.target, trackerName);
                }}
              />
              {trackerName === "matomo" && (
                <React.Fragment>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label>{t("matomo_site_url")}</label>
                  <input
                    className="editor-form-input-title input-right"
                    type="text"
                    value={getTrackerConfig(trackerName).siteUrl}
                    onChange={(e): void => {
                      handleChangeTracker(e.target, trackerName, "siteUrl");
                    }}
                  />
                </React.Fragment>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="editor-form-section section-separator">
        <div className="editor-form-translates">
          <div className="editor-form-section-title">{t("integration")}</div>
          {INTEGRATION_TAG_LIST.map((trackerName) => (
            <div key={trackerName} className="editor-form-translate">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label>{t(trackerName)}</label>
              <input
                className="editor-form-input-title"
                type="text"
                value={getTrackerConfig(trackerName)?.id}
                onChange={(e): void => {
                  handleChangeTracker(e.target, trackerName);
                }}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="editor-form-section section-separator">
        <div className="editor-form-translates">
          <div className="editor-form-section-title">
            <div>{t("social_network_links")}</div>
            <InfoTooltip>{t("socialNetworksTooltip")}</InfoTooltip>
          </div>
          {[
            "facebook",
            "twitter",
            "instagram",
            "youtube",
            "linkedin",
            "tiktok",
            "threads",
          ].map((socialName) => (
            <div key={socialName} className="editor-form-translate">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label>{t(socialName)}</label>
              <input
                className="editor-form-input-title"
                type="text"
                value={getSocialLink(socialName).uid}
                onChange={(e): void => {
                  handleChangeSocialLink(e.target, socialName);
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </React.Fragment>
  );
}

export default ProjectAdminFormSocialTrackingAndSocialNetwork;
